/**
 * browserMcpTraffic.ts
 *
 * Provides bounded per-tab MCP tool-call observability for the browser runtime.
 *
 * Design follows the same pattern as browserHttpErrors.ts:
 * - 50-entry bounded buffer (MAX_RUNTIME_MCP_TOOL_CALLS)
 * - sessionStorage persistence (same tab lifetime as HTTP errors)
 * - Redacted previews only — not full params/result capture
 * - Allowlist approach: only instruments RemoteMcpClient used by the webapp
 *
 * This module is the SDK-side primitive; instrumentation lives in
 * webapp/src/engine/mcp/remoteClient.ts.
 */

export interface McpToolCallEntry {
    /** JSON-RPC method name, e.g. "tools/call". */
    readonly method: string
    /** Connection label from the MCP server config, e.g. "Primary MCP". */
    readonly connectionLabel: string
    /** 'remote' for RemoteMcpClient; 'local' for §157/§158 localhost MCP. */
    readonly connectionType: 'remote' | 'local'
    /** Client-generated request ID, e.g. "mcp-1". */
    readonly callId: string
    /** ISO timestamp when the request was sent. */
    readonly capturedAt: string
    /** Wall-clock ms from send to receive. */
    readonly durationMs: number
    /** True when the server returned a JSON-RPC error. */
    readonly isError: boolean
    /** Redacted preview of params (null if none). Max 200 chars. */
    readonly redactedParams: string | null
    /** Redacted preview of result/error detail. Max 200 chars. */
    readonly redactedResult: string | null
}

export const MAX_RUNTIME_MCP_TOOL_CALLS = 50

const RUNTIME_MCP_TRAFFIC_STORAGE_KEY = 'sb:runtime-mcp-tool-calls'

let runtimeMcpToolCalls: McpToolCallEntry[] = []

function isMcpToolCallEntry(value: unknown): value is McpToolCallEntry {
    return typeof value === 'object'
        && value !== null
        && typeof (value as McpToolCallEntry).method === 'string'
        && typeof (value as McpToolCallEntry).callId === 'string'
        && typeof (value as McpToolCallEntry).capturedAt === 'string'
}

function readPersistedMcpToolCalls(): McpToolCallEntry[] {
    if (typeof sessionStorage === 'undefined') {
        return runtimeMcpToolCalls
    }

    try {
        const raw = sessionStorage.getItem(RUNTIME_MCP_TRAFFIC_STORAGE_KEY)
        if (!raw) {
            return runtimeMcpToolCalls
        }

        const parsed = JSON.parse(raw)
        if (!Array.isArray(parsed)) {
            return runtimeMcpToolCalls
        }

        const normalized = parsed
            .filter(isMcpToolCallEntry)
            .slice(-MAX_RUNTIME_MCP_TOOL_CALLS)
        const normalizedRaw = normalized.length > 0 ? JSON.stringify(normalized) : null
        if (normalizedRaw !== raw) {
            persistMcpToolCalls(normalized)
            return normalized
        }

        runtimeMcpToolCalls = normalized
        return normalized
    } catch {
        return runtimeMcpToolCalls
    }
}

function persistMcpToolCalls(entries: McpToolCallEntry[]): void {
    runtimeMcpToolCalls = entries

    if (typeof sessionStorage === 'undefined') {
        return
    }

    try {
        if (entries.length === 0) {
            sessionStorage.removeItem(RUNTIME_MCP_TRAFFIC_STORAGE_KEY)
            return
        }

        sessionStorage.setItem(RUNTIME_MCP_TRAFFIC_STORAGE_KEY, JSON.stringify(entries))
    } catch {
        // Best-effort persistence only.
    }
}

const REDACT_PREVIEW_MAX = 200

function redact(value: unknown): string {
    if (value === null || value === undefined) {
        return null as unknown as string
    }

    let serialized: string
    try {
        serialized = JSON.stringify(value)
    } catch {
        serialized = String(value)
    }

    if (serialized.length <= REDACT_PREVIEW_MAX) {
        return serialized
    }

    return serialized.slice(0, REDACT_PREVIEW_MAX) + '…'
}

/**
 * Record a single MCP tool-call round-trip into the bounded per-tab buffer.
 * Call this in RemoteMcpClient.sendRequest() after the response is received.
 */
export function recordMcpToolCall(entry: Omit<McpToolCallEntry, never>): McpToolCallEntry {
    const normalized: McpToolCallEntry = {
        method: entry.method,
        connectionLabel: entry.connectionLabel,
        connectionType: entry.connectionType,
        callId: entry.callId,
        capturedAt: entry.capturedAt,
        durationMs: entry.durationMs,
        isError: entry.isError,
        redactedParams: entry.redactedParams,
        redactedResult: entry.redactedResult,
    }

    const entries = [...readPersistedMcpToolCalls(), normalized].slice(-MAX_RUNTIME_MCP_TOOL_CALLS)
    persistMcpToolCalls(entries)
    return normalized
}

/**
 * Retrieve recent MCP tool calls from the per-tab buffer.
 * Returns a new array; mutating the return value does not affect the buffer.
 */
export function getRecentMcpToolCalls(limit = MAX_RUNTIME_MCP_TOOL_CALLS): McpToolCallEntry[] {
    const entries = readPersistedMcpToolCalls()
    if (!Number.isFinite(limit) || limit >= MAX_RUNTIME_MCP_TOOL_CALLS) {
        return [...entries]
    }

    const normalizedLimit = Math.max(0, Math.trunc(limit))
    if (normalizedLimit === 0) {
        return []
    }

    return entries.slice(-normalizedLimit)
}

/** Clear all buffered MCP tool calls for the current tab. */
export function clearRecentMcpToolCalls(): void {
    persistMcpToolCalls([])
}

/** Format a single MCP tool call entry for human-readable output. */
export function formatMcpToolCall(entry: McpToolCallEntry): string {
    const error = entry.isError ? ' [ERROR]' : ''
    const params = entry.redactedParams ? ` params=${entry.redactedParams}` : ''
    const result = entry.redactedResult ? ` -> ${entry.redactedResult}` : ''
    return `${entry.callId} ${entry.method}${error} (${entry.durationMs}ms)${params}${result}`
}

/** Format an array of MCP tool call entries, one per line. */
export function formatMcpToolCalls(entries: readonly McpToolCallEntry[]): string {
    return entries.map((entry, index) => `${index + 1}. ${formatMcpToolCall(entry)}`).join('\n')
}

/* ------------------------------------------------------------------ */
/* §254: SQLite write-lane observability                              */
/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */
/* §205: SQL debug snapshot                                           */
/* ------------------------------------------------------------------ */

/**
 * Structured snapshot of the last MCP SQL tool execution.
 * Captures the full provenance that §159's redacted buffer omits:
 * tool name, query text, params, workspace/path, source/lane, row count, duration, error.
 *
 * Stored separately from the general MCP tool-call buffer because the
 * snapshot is a single "last SQL result" signal, not part of the 50-entry
 * general-purpose call history.
 */
export interface SqlQueryDebugSnapshot {
    readonly callId: string
    readonly capturedAt: string
    readonly toolName: 'secureblob_sql_query' | 'secureblob_sql_schema' | 'secureblob_sql_execute'
    readonly query: string | null          // null for schema tool
    readonly params: Record<string, unknown> | null
    readonly workspaceRef: string
    readonly path: string
    readonly source: string                 // 'sqlite3mt-lane' | 'raw-file'
    readonly lane: string | null            // null for raw-file source
    readonly rowCount: number | null        // null for schema/execute tools
    readonly rowsAffected: number | null    // null for query/schema tools
    readonly lastInsertRowid: number | null // null unless execute reports it
    readonly transactionMode: string | null // null for query/schema tools
    readonly changed: boolean | null        // null for query/schema tools
    readonly changedPageCount: number | null
    readonly pageCount: number | null
    readonly durationMs: number
    readonly error: string | null
}

type SqlQueryDebugSnapshotListener = () => void

let sqlQueryDebugSnapshot: SqlQueryDebugSnapshot | null = null
const sqlQueryDebugSnapshotListeners = new Set<SqlQueryDebugSnapshotListener>()

function notifySqlQueryDebugSnapshotListeners(): void {
    for (const listener of sqlQueryDebugSnapshotListeners) {
        listener()
    }
}

/**
 * Record a SQL-tool result as the current debug snapshot.
 * Called in RemoteMcpClient after a tools/call response is parsed,
 * when the tool name is secureblob_sql_query, secureblob_sql_schema,
 * or secureblob_sql_execute.
 *
 * Pass the parsed resultData from the MCP response (which carries
 * path / source / lane provenance per §201) and the duration.
 */
export function recordSqlQueryDebugSnapshot(
    callId: string,
    capturedAt: string,
    toolName: 'secureblob_sql_query' | 'secureblob_sql_schema' | 'secureblob_sql_execute',
    query: string | null,
    params: Record<string, unknown> | null,
    workspaceRef: string,
    path: string,
    source: string,
    lane: string | null,
    rowCount: number | null,
    rowsAffected: number | null,
    lastInsertRowid: number | null,
    transactionMode: string | null,
    changed: boolean | null,
    changedPageCount: number | null,
    pageCount: number | null,
    durationMs: number,
    error: string | null,
): SqlQueryDebugSnapshot {
    const snapshot: SqlQueryDebugSnapshot = {
        callId,
        capturedAt,
        toolName,
        query,
        params,
        workspaceRef,
        path,
        source,
        lane,
        rowCount,
        rowsAffected,
        lastInsertRowid,
        transactionMode,
        changed,
        changedPageCount,
        pageCount,
        durationMs,
        error,
    }
    sqlQueryDebugSnapshot = snapshot
    notifySqlQueryDebugSnapshotListeners()
    return snapshot
}

/**
 * Retrieve the last SQL query debug snapshot, or null if no SQL tool
 * has been captured in this tab session.
 */
export function getSqlQueryDebugSnapshot(): SqlQueryDebugSnapshot | null {
    return sqlQueryDebugSnapshot
}

/** Subscribe to changes to the last SQL query debug snapshot. */
export function subscribeSqlQueryDebugSnapshot(listener: SqlQueryDebugSnapshotListener): () => void {
    sqlQueryDebugSnapshotListeners.add(listener)
    return () => {
        sqlQueryDebugSnapshotListeners.delete(listener)
    }
}

/**
 * Clear the SQL query debug snapshot.
 * Called on workspace/session clear so stale provenance doesn't mislead
 * after a context switch.
 */
export function clearSqlQueryDebugSnapshot(): void {
    if (sqlQueryDebugSnapshot === null) {
        return
    }
    sqlQueryDebugSnapshot = null
    notifySqlQueryDebugSnapshotListeners()
}

/* ------------------------------------------------------------------ */
/* §307: Lane Head History Trace                                      */
/* ------------------------------------------------------------------ */

/**
 * A single lane-head history entry recording which surface updated
 * the lane head, at what page count, and the delta from the previous
 * entry for the same surface.
 *
 * Follows the same bounded-buffer pattern as the MCP traffic and
 * SQL debug snapshot observability modules.
 */
export interface LaneHeadHistoryEntry {
    /** Which surface caused the update. */
    readonly surface: 'browser-sql-explorer' | 'mcp-client' | 'component-job'
    /** Workspace scope for the active database. */
    readonly workspaceRef: string
    /** Database path scope for the active database. */
    readonly path: string
    /** Lane identifier. */
    readonly lane: string
    /** Total page count after this update. */
    readonly pageCount: number
    /** Page delta from the previous entry for the same surface. */
    readonly pageDelta: number
    /** Cumulative commit count for this lane. */
    readonly commitCount: number
    /** Timestamp in ms since epoch. */
    readonly capturedAt: number
    /** Transaction mode used for this commit. */
    readonly transactionMode: 'sequential' | 'concurrent' | 'exclusive'
}

export interface LaneHeadHistoryScope {
    readonly workspaceRef?: string | null
    readonly path?: string | null
}

export const MAX_LANE_HEAD_HISTORY = 20

const LANE_HEAD_HISTORY_STORAGE_KEY = 'sb:lane-head-history'

type LaneHeadHistoryListener = () => void

let laneHeadHistory: LaneHeadHistoryEntry[] = []
let laneHeadHistoryVersion = 0
const laneHeadHistoryListeners = new Set<LaneHeadHistoryListener>()
const laneHeadHistoryViewCache = new Map<string, { source: LaneHeadHistoryEntry[]; value: LaneHeadHistoryEntry[] }>()
const EMPTY_LANE_HEAD_HISTORY: LaneHeadHistoryEntry[] = []

function normalizeLaneHeadHistoryEntry(value: unknown): LaneHeadHistoryEntry | null {
    if (typeof value !== 'object' || value === null) {
        return null
    }

    const entry = value as Partial<LaneHeadHistoryEntry>
    if (
        typeof entry.surface !== 'string'
        || typeof entry.lane !== 'string'
        || typeof entry.pageCount !== 'number'
        || typeof entry.capturedAt !== 'number'
    ) {
        return null
    }

    const surface = entry.surface === 'browser-sql-explorer'
        ? 'browser-sql-explorer'
        : entry.surface === 'component-job'
            ? 'component-job'
            : entry.surface === 'mcp-client'
                ? 'mcp-client'
                : null
    if (surface === null) {
        return null
    }

    return {
        surface,
        workspaceRef: typeof entry.workspaceRef === 'string' ? entry.workspaceRef : '',
        path: typeof entry.path === 'string' ? entry.path : '',
        lane: entry.lane,
        pageCount: entry.pageCount,
        pageDelta: typeof entry.pageDelta === 'number' ? entry.pageDelta : entry.pageCount,
        commitCount: typeof entry.commitCount === 'number' ? entry.commitCount : 0,
        capturedAt: entry.capturedAt,
        transactionMode: entry.transactionMode === 'exclusive'
            ? 'exclusive'
            : entry.transactionMode === 'sequential'
                ? 'sequential'
                : 'concurrent',
    }
}

function matchesLaneHeadScope(entry: LaneHeadHistoryEntry, scope?: LaneHeadHistoryScope): boolean {
    const workspaceRef = scope?.workspaceRef ?? ''
    const path = scope?.path ?? ''

    return (workspaceRef === '' || entry.workspaceRef === workspaceRef)
        && (path === '' || entry.path === path)
}

function readPersistedLaneHeadHistory(): LaneHeadHistoryEntry[] {
    if (typeof sessionStorage === 'undefined') {
        return laneHeadHistory
    }

    try {
        const raw = sessionStorage.getItem(LANE_HEAD_HISTORY_STORAGE_KEY)
        if (!raw) {
            return laneHeadHistory
        }

        const parsed = JSON.parse(raw)
        if (!Array.isArray(parsed)) {
            return laneHeadHistory
        }

        const normalized = parsed
            .map(normalizeLaneHeadHistoryEntry)
            .filter((entry): entry is LaneHeadHistoryEntry => entry !== null)
            .slice(-MAX_LANE_HEAD_HISTORY)
        const normalizedRaw = normalized.length > 0 ? JSON.stringify(normalized) : null
        if (normalizedRaw !== raw) {
            persistLaneHeadHistory(normalized)
            return normalized
        }

        laneHeadHistory = normalized
        return normalized
    } catch {
        return laneHeadHistory
    }
}

function persistLaneHeadHistory(entries: LaneHeadHistoryEntry[]): void {
    laneHeadHistory = entries
    laneHeadHistoryVersion += 1
    laneHeadHistoryViewCache.clear()

    if (typeof sessionStorage === 'undefined') {
        return
    }

    try {
        if (entries.length === 0) {
            sessionStorage.removeItem(LANE_HEAD_HISTORY_STORAGE_KEY)
            return
        }

        sessionStorage.setItem(LANE_HEAD_HISTORY_STORAGE_KEY, JSON.stringify(entries))
    } catch {
        // Best-effort persistence only.
    }
}

function notifyLaneHeadHistoryListeners(): void {
    for (const listener of laneHeadHistoryListeners) {
        listener()
    }
}

/**
 * Record a lane head history entry. Computes pageDelta automatically
 * by comparing against the last entry for the same surface+database scope+lane pair.
 */
export function recordLaneHeadHistory(
    surface: LaneHeadHistoryEntry['surface'],
    lane: string,
    pageCount: number,
    commitCount: number,
    transactionMode: LaneHeadHistoryEntry['transactionMode'],
    scope?: LaneHeadHistoryScope,
): LaneHeadHistoryEntry {
    const entries = readPersistedLaneHeadHistory()
    const workspaceRef = scope?.workspaceRef ?? ''
    const path = scope?.path ?? ''

    // Find last entry for same surface+database scope+lane to compute delta
    let pageDelta = pageCount
    for (let i = entries.length - 1; i >= 0; i--) {
        if (
            entries[i].surface === surface
            && entries[i].workspaceRef === workspaceRef
            && entries[i].path === path
            && entries[i].lane === lane
        ) {
            pageDelta = pageCount - entries[i].pageCount
            break
        }
    }

    const entry: LaneHeadHistoryEntry = {
        surface,
        workspaceRef,
        path,
        lane,
        pageCount,
        pageDelta,
        commitCount,
        capturedAt: Date.now(),
        transactionMode,
    }

    const updated = [...entries, entry].slice(-MAX_LANE_HEAD_HISTORY)
    persistLaneHeadHistory(updated)
    notifyLaneHeadHistoryListeners()
    return entry
}

/**
 * Retrieve recent lane head history entries.
 * Returns a cached immutable view that stays stable until the buffer changes,
 * so React external-store consumers can subscribe without render loops.
 */
export function getLaneHeadHistory(limit = MAX_LANE_HEAD_HISTORY, scope?: LaneHeadHistoryScope): LaneHeadHistoryEntry[] {
    const entries = readPersistedLaneHeadHistory()
    const normalizedLimit = Math.max(0, Math.trunc(limit))
    if (normalizedLimit === 0) {
        return EMPTY_LANE_HEAD_HISTORY
    }

    const workspaceRef = scope?.workspaceRef ?? ''
    const path = scope?.path ?? ''
    const cacheKey = `${workspaceRef}\u0000${path}\u0000${Number.isFinite(limit) ? Math.min(normalizedLimit, MAX_LANE_HEAD_HISTORY) : MAX_LANE_HEAD_HISTORY}`
    const cached = laneHeadHistoryViewCache.get(cacheKey)
    if (cached?.source === entries) {
        return cached.value
    }

    const scopedEntries = entries.filter(entry => matchesLaneHeadScope(entry, scope))
    const value = (!Number.isFinite(limit) || limit >= MAX_LANE_HEAD_HISTORY)
        ? [...scopedEntries]
        : scopedEntries.slice(-normalizedLimit)

    laneHeadHistoryViewCache.set(cacheKey, { source: entries, value })
    return value
}

/** Clear all lane head history entries for the current tab. */
export function clearLaneHeadHistory(): void {
    persistLaneHeadHistory([])
    notifyLaneHeadHistoryListeners()
}

/** Subscribe to changes in the lane head history buffer. */
export function subscribeLaneHeadHistory(listener: LaneHeadHistoryListener): () => void {
    laneHeadHistoryListeners.add(listener)
    return () => {
        laneHeadHistoryListeners.delete(listener)
    }
}

/** Monotonic version for React external-store consumers of lane head history. */
export function getLaneHeadHistoryVersion(): number {
    return laneHeadHistoryVersion
}

/**
 * Format a lane head history entry for human-readable display.
 * Returns a compact single-line string.
 */
export function formatLaneHeadHistoryEntry(entry: LaneHeadHistoryEntry): string {
    const delta = entry.pageDelta >= 0 ? `+${entry.pageDelta}` : `${entry.pageDelta}`
    const age = formatLaneHeadAge(Date.now() - entry.capturedAt)
    return `${entry.surface} · lane ${entry.lane.slice(0, 7)}… · ${delta} pages · commit #${entry.commitCount} · ${age} · ${entry.transactionMode}`
}

function formatLaneHeadAge(ms: number): string {
    if (ms < 1000) return 'just now'
    if (ms < 60_000) return `${Math.round(ms / 1000)}s ago`
    if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m ago`
    return `${Math.round(ms / 3_600_000)}h ago`
}
